import { useEffect, useMemo, useRef } from 'react';
import { inject, observer } from 'mobx-react';
import LinearItemWrap from '@/components/LinearItemWrap';
import VersionLabel from '@/pages/product/components/VersionWrapper/components/VersionLabel';
import { getVersionName } from '@/pages/product/config/ProductVersion.config';
import { formatPriceThousands } from '@/utils/configs/price.config';
import { ProductTracker } from '@/utils/Tracker';
import LazyLoadImg from '@/components/LazyLoadImg';
import { isEmpty, scrollToEle } from '@/utils';
import cls from 'classnames';
import ComponentsErrorBoundary from '@/components/ErrorBoundary/ComponentsErrorBoundary';
import ItemGiftsWrap from '../ItemGiftsWrap/index.mobile';
import gsap from 'gsap';

import styles from './CommodityItem.module.less';
import ossImgCompress from '@/utils/ossImgCompress';
import dynamic from 'next/dynamic';
import { LabelStyle } from '@/pages/product/config/productLabel.config';
import { PrimeTagCommodityMap } from '@/utils/configs/prime.config';

const CloudWrap = dynamic(import('../CloudWrap'), { ssr: false });
const PrimeWrap = dynamic(import('../PrimeWrap'), { ssr: false });

const arrowIcon = 'https://res.insta360.com/static/6a6ff0166d55d440e61feca1a934174a/Frame%201973770998.png';
const defaultImg = 'https://static.insta360.com/assets/storage/20191218/f2a97117d5c7b21d3e9f9b28dad343f5/1.png';

/**
 * 套餐单个选项item
 * @param {function} onChangeCommodity 点选后回调
 * @param {array} labels item右上角的标签
 * @param {object} commodity 套餐信息 / 规格则传入第一个套餐的信息
 * @param {string} className
 */
function Index(props) {
  const {
    labels,
    onChangeCommodity,
    commodity = {},
    commodity: { isOutOfStock },
    onShowModal,
    commodity: { product: pid, price, origin_price, info: cInfo, id: cid },
    className,
    product: { productDetail, curSelectCommodityId, isSelectPersonalized, carePlusPrice },
    area: {
      country,
      country: { X_Language, code },
    },
    app: { campaignWrap, webp, isMobile },
  } = props;

  const itemRef = useRef(null);
  /** 是否首次加载 */
  const firstRef = useRef(false);

  /** 套餐信息node */
  const itemIntroRef = useRef(null);

  /** 赠品 */
  const giftsRef = useRef(null);

  // 当前item是否选中,根据curSelectCommodity自动判断
  const active = useMemo(
    () => !isSelectPersonalized && curSelectCommodityId === cid,
    [cid, curSelectCommodityId, isSelectPersonalized]
  );

  // 卡片收起动画
  const onFold = (duration) => {
    requestAnimationFrame(() => {
      gsap.to(itemIntroRef.current, {
        duration: duration,
        height: 0,
      });
    });
  };

  // 卡片展开动画：先定位再展开套餐描述
  const onSpread = () => {
    requestAnimationFrame(() => {
      gsap.to(itemIntroRef.current, {
        duration: 0.25,
        height: itemIntroRef.current?.scrollHeight,
        ease: 'none',
        onStart: () => {
          requestAnimationFrame(() => {
            scrollToEle(itemIntroRef.current, 60);
          });
        },
        onComplete: () => {},
      });
    });
  };

  // 切换套餐后进行定位
  useEffect(() => {
    if (!firstRef.current) {
      firstRef.current = true;
      if (!active) {
        onFold(0);
      }
      return;
    }

    // 切换套餐会引起高度改变
    if (active) {
      onSpread();
    } else {
      onFold(0);
    }
  }, [active]);

  /** 选中套餐 */
  const handleClick = () => {
    onChangeCommodity(cid);
    ProductTracker.productPage({
      action: 'commodity_select',
      productState: props.product,
    });
  };

  const campaignExtraInfo = useMemo(
    () => campaignWrap?.getCampaignExtraInfo({ pid, cid, productDetail, commodity }),
    [cid]
  );

  const showItemIntro = useMemo(() => {
    if (isEmpty(cInfo.introduction, true) && isEmpty(campaignExtraInfo)) return false;
    return true;
  }, [cInfo.introduction, campaignExtraInfo]);

  const cinfos = useMemo(() => {
    if (!cInfo.introduction) return '';
    // 设置carePlus简介中的价格变量
    if (commodity.bind_service) {
      return cInfo.introduction.replace(
        '__PRICE__',
        formatPriceThousands({
          price: commodity.price.amount,
          currency: commodity.price.currency,
          signal: commodity.price.signal,
          country,
        })
      );
    } else return cInfo.introduction;
  }, [cInfo.introduction, carePlusPrice, cid]);

  const commodityLabels = useMemo(() => {
    const _labels = [];

    const PrimeSvg = () => {
      return (
        <svg width='45' height='12' viewBox='0 0 45 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <g clip-path='url(#clip0_386_6319)'>
            <path
              d='M14.0581 4.15918C13.6021 4.15907 13.1546 4.27673 12.763 4.49971V7.81568C13.1518 8.04538 13.6008 8.16562 14.0581 8.16258C14.55 8.16258 14.9131 8.00316 15.1474 7.68432C15.3818 7.36547 15.4985 6.86085 15.4976 6.17045C15.4976 5.47409 15.3831 4.96607 15.1541 4.64638C14.9256 4.32626 14.5593 4.16683 14.0581 4.16683M10.8356 3.27662C10.8304 3.23273 10.8348 3.18828 10.8486 3.14612C10.8625 3.10395 10.8853 3.065 10.9158 3.03175C10.9875 2.97929 11.0771 2.95427 11.1671 2.96161H12.1174C12.2047 2.95387 12.2918 2.9784 12.3607 3.03013C12.4295 3.08187 12.475 3.15686 12.4876 3.23964L12.5799 3.57123C12.856 3.31992 13.1811 3.12277 13.5368 2.99094C13.9003 2.852 14.2881 2.78057 14.6796 2.7805C15.535 2.7805 16.2109 3.07809 16.7072 3.67326C17.2035 4.26844 17.4526 5.07618 17.4544 6.09648C17.468 6.72382 17.3418 7.34677 17.0841 7.92409C16.8715 8.40241 16.5269 8.81673 16.087 9.12294C15.6586 9.405 15.1479 9.55094 14.6275 9.53999C14.2768 9.5414 13.9284 9.4862 13.597 9.37674C13.2912 9.2784 13.0084 9.12419 12.7643 8.92271V11.6852C12.7739 11.7726 12.7481 11.8602 12.6921 11.93C12.6186 11.9838 12.5264 12.0089 12.4342 12.0002H11.1671C11.0771 12.0075 10.9875 11.9825 10.9158 11.93C10.8853 11.8968 10.8625 11.8578 10.8486 11.8157C10.8348 11.7735 10.8304 11.7291 10.8356 11.6852V3.27662Z'
              fill='#1A98FF'
            />
            <path
              d='M20.5699 3.92431C20.8525 3.60796 21.1897 3.34009 21.567 3.1323C21.8748 2.97944 22.2172 2.90105 22.5641 2.90401H22.7459C22.8408 2.89502 22.9359 2.91952 23.0132 2.97288C23.0437 3.00637 23.0666 3.04554 23.0804 3.08791C23.0942 3.13029 23.0986 3.17494 23.0934 3.21903V4.27759C23.1003 4.36339 23.0742 4.44865 23.0199 4.51736C22.9848 4.54648 22.9437 4.56832 22.8993 4.58149C22.8549 4.59467 22.8081 4.59889 22.7619 4.59388C22.6828 4.59513 22.6038 4.59087 22.5253 4.58113C22.4023 4.56951 22.2787 4.56525 22.1551 4.56838C21.9067 4.57341 21.6592 4.60031 21.416 4.64872C21.1621 4.6933 20.9139 4.76341 20.6755 4.85789V9.0883C20.6838 9.17364 20.658 9.2588 20.6033 9.3268C20.5682 9.35592 20.5272 9.37776 20.4828 9.39093C20.4384 9.4041 20.3916 9.40832 20.3454 9.40332H19.077C19.0032 9.41447 18.9277 9.39763 18.8668 9.3564C18.8058 9.31516 18.7642 9.25281 18.7508 9.18268C18.7455 9.15228 18.7455 9.12125 18.7508 9.09085V3.27642C18.7461 3.23245 18.7507 3.18802 18.7642 3.14575C18.7777 3.10348 18.8 3.06422 18.8297 3.03027C18.902 2.97916 18.9914 2.95511 19.081 2.96268H20.0326C20.1195 2.95488 20.2062 2.97906 20.2751 3.03027C20.3439 3.08148 20.3896 3.15585 20.4028 3.23816L20.5699 3.92431Z'
              fill='#1A98FF'
            />
            <path
              d='M24.025 3.27636C24.0203 3.2324 24.0251 3.18797 24.0391 3.14585C24.0531 3.10373 24.0761 3.0648 24.1065 3.03149C24.1782 2.97903 24.2678 2.95402 24.3578 2.96135H25.6262C25.718 2.95257 25.8098 2.97767 25.8828 3.03149C25.9392 3.10115 25.9655 3.18877 25.9563 3.27636V9.08697C25.964 9.17284 25.9378 9.25836 25.8828 9.32674C25.8479 9.3556 25.807 9.3772 25.7628 9.39015C25.7186 9.40311 25.6721 9.40714 25.6262 9.40199H24.3578C24.2834 9.41429 24.207 9.39818 24.1449 9.35713C24.0829 9.31608 24.0403 9.2534 24.0263 9.18262C24.021 9.1518 24.021 9.12035 24.0263 9.08952L24.025 3.27636ZM24.99 1.96655C24.697 1.97886 24.4102 1.88397 24.188 1.70128C24.0863 1.60947 24.0065 1.49779 23.9542 1.37387C23.9018 1.24995 23.8781 1.11671 23.8846 0.98324C23.8781 0.84958 23.9018 0.716141 23.9541 0.592011C24.0065 0.467882 24.0863 0.35598 24.188 0.263929C24.4185 0.0928726 24.7021 0 24.994 0C25.2859 0 25.5696 0.0928726 25.8 0.263929C25.9017 0.35598 25.9815 0.467882 26.0339 0.592011C26.0862 0.716141 26.1099 0.84958 26.1034 0.98324C26.1099 1.11671 26.0862 1.24995 26.0338 1.37387C25.9815 1.49779 25.9017 1.60947 25.8 1.70128C25.5776 1.88352 25.2909 1.97834 24.998 1.96655'
              fill='#1A98FF'
            />
            <path
              d='M29.3459 3.57996C29.7379 3.32207 30.1622 3.11213 30.609 2.95502C30.981 2.83527 31.3712 2.77495 31.7638 2.77647C32.5568 2.77647 33.116 3.04558 33.4412 3.58378C33.8336 3.32517 34.2601 3.11718 34.7096 2.96523C35.1059 2.84019 35.5206 2.77647 35.9379 2.77647C36.5545 2.77647 37.0321 2.94014 37.3707 3.26749C37.7093 3.59484 37.8791 4.05312 37.88 4.64235V9.08703C37.8877 9.1733 37.8615 9.25924 37.8065 9.32807C37.7712 9.35644 37.7303 9.37764 37.6862 9.39036C37.642 9.40308 37.5956 9.40706 37.5498 9.40204H36.2814C36.2446 9.40847 36.2069 9.40788 36.1703 9.40032C36.1338 9.39275 36.0992 9.37836 36.0685 9.35796C36.0378 9.33757 36.0116 9.31159 35.9915 9.2815C35.9714 9.25142 35.9577 9.21783 35.9513 9.18268C35.9446 9.15025 35.9446 9.1169 35.9513 9.08448V5.04154C35.9513 4.47017 35.684 4.18449 35.1493 4.18449C34.6485 4.18939 34.1553 4.3014 33.7058 4.51226V9.08703C33.7137 9.17292 33.6885 9.25862 33.635 9.32807C33.5995 9.35648 33.5584 9.37769 33.514 9.39041C33.4696 9.40312 33.423 9.40708 33.377 9.40204H32.1086C32.0634 9.41001 32.0169 9.40738 31.973 9.39437C31.9291 9.38135 31.8892 9.35836 31.8568 9.32735C31.8243 9.29635 31.8002 9.25828 31.7865 9.21641C31.7729 9.17454 31.7701 9.13014 31.7785 9.08703V5.04154C31.7785 4.47017 31.5112 4.18449 30.9765 4.18449C30.4711 4.1877 29.9735 4.30389 29.5237 4.52374V9.08703C29.5319 9.17315 29.5062 9.25912 29.4515 9.32807C29.4153 9.35851 29.3728 9.38138 29.3267 9.39521C29.2807 9.40905 29.2321 9.41355 29.1842 9.40842H27.9158C27.8792 9.41487 27.8416 9.41438 27.8052 9.40698C27.7688 9.39957 27.7343 9.3854 27.7036 9.36527C27.673 9.34513 27.6468 9.31943 27.6265 9.28964C27.6063 9.25984 27.5924 9.22653 27.5856 9.19161C27.579 9.15918 27.579 9.12583 27.5856 9.0934V3.27642C27.5809 3.23264 27.5854 3.1884 27.5989 3.14633C27.6125 3.10426 27.6348 3.06522 27.6645 3.03155C27.7365 2.97974 27.8258 2.9548 27.9158 2.9614H28.8674C28.9544 2.95432 29.041 2.97913 29.1095 3.03077C29.178 3.08242 29.2234 3.15703 29.2363 3.23943L29.3459 3.57996Z'
              fill='#1A98FF'
            />
            <path
              d='M42.2386 4.05182C41.393 4.05182 40.9029 4.54794 40.7684 5.54018C41.1561 5.6096 41.55 5.64292 41.9446 5.63966C42.3901 5.63966 42.7158 5.57462 42.9216 5.44453C43.0228 5.37898 43.1042 5.28924 43.1577 5.18428C43.2111 5.07932 43.2348 4.9628 43.2263 4.84638C43.2263 4.31752 42.8958 4.05267 42.2346 4.05182M42.0542 6.76837C41.6137 6.77541 41.1741 6.72956 40.7456 6.6319C40.7991 7.19392 40.9773 7.59693 41.2803 7.84096C41.5832 8.08498 42.0435 8.20656 42.661 8.20571C42.9014 8.20554 43.1416 8.19063 43.38 8.16108C43.7074 8.11273 44.0318 8.04759 44.3517 7.96594C44.3773 7.95703 44.4037 7.95062 44.4306 7.94681C44.4524 7.94554 44.4743 7.94554 44.4961 7.94681C44.6458 7.94681 44.7206 8.04374 44.7206 8.2376V8.81662C44.7289 8.91437 44.7085 9.01239 44.6618 9.09975C44.6007 9.16808 44.5205 9.21849 44.4306 9.24514C43.7765 9.48138 43.0824 9.6007 42.383 9.59715C41.2905 9.59715 40.4516 9.31189 39.8662 8.74137C39.2807 8.17085 38.988 7.35164 38.988 6.28372C38.988 5.19115 39.2874 4.33368 39.8862 3.71129C40.485 3.08891 41.3177 2.77729 42.3843 2.77644C43.2032 2.77644 43.8438 2.96562 44.3063 3.34399C44.5344 3.52954 44.7154 3.7621 44.8355 4.02413C44.9557 4.28615 45.0119 4.57079 45 4.85658C45 5.48661 44.7509 5.9636 44.2528 6.28755C43.7547 6.6115 43.0218 6.77304 42.0542 6.77219'
              fill='#1A98FF'
            />
            <path
              d='M10.0363 2.95493H8.44447C8.36294 2.95493 8.13304 2.92304 8.07156 3.06206L4.49888 8.54489L2.88562 6.5923C2.86419 6.56137 2.83498 6.53607 2.80066 6.51869C2.76633 6.50131 2.728 6.49242 2.68914 6.49282H0.928867C0.866584 6.49281 0.806811 6.51625 0.762521 6.55803C0.718231 6.59981 0.692996 6.65658 0.692292 6.71601C0.692177 6.76728 0.710058 6.81712 0.743082 6.85757L4.44809 11.2576C4.46994 11.2852 4.49824 11.3075 4.53073 11.3228C4.56321 11.338 4.59899 11.3459 4.63521 11.3456H4.6459C4.68401 11.344 4.72114 11.3336 4.75417 11.3154C4.7872 11.2972 4.81516 11.2717 4.8357 11.241L10.1486 3.386C10.3638 3.10415 10.1232 2.95493 10.0363 2.95493Z'
              fill='#FF9900'
            />
          </g>
          <defs>
            <clipPath id='clip0_386_6319'>
              <rect x='0.692322' width='44.3077' height='12' rx='6' fill='white' />
            </clipPath>
          </defs>
        </svg>
      );
    };

    // 如果当前商品是Prime商品，则添加Prime标签
    if (PrimeTagCommodityMap.includes(cid?.toString()) && code === 'US') {
      _labels.push({
        ...LabelStyle.prime,
        tagContent: <PrimeSvg />,
      });
    }
    return [..._labels, ...labels];
  }, [cid, labels, code]);

  if (!cid) return null;

  return (
    <div className={[styles.itemContainer, className].join(' ')} id={'id' + pid + cid} ref={itemRef}>
      {isMobile && (
        <div className={styles.imgBtn} data-active={active}>
          <LazyLoadImg src={arrowIcon} />
        </div>
      )}
      {/* 按钮热区，选中态才能打开弹窗 */}
      {isMobile && (
        <div className={styles.btnHotspot} data-active={active} onClick={() => active && onShowModal(commodity)} />
      )}

      <div className={styles.labelWrap}>
        {commodityLabels?.map((label, k) => (
          <div className={styles.label} key={k}>
            <VersionLabel label={label} />
          </div>
        ))}
      </div>
      <ComponentsErrorBoundary key={cid} name='CommodityItem LinearItemWrap' data={{ cid, active, commodity }}>
        <LinearItemWrap
          key={cid}
          onClick={handleClick}
          theme={isOutOfStock ? 'outOfStock' : 'productBlack'}
          active={`${active}`}
          className={cls(styles.commodityItem, isOutOfStock && styles.disable)}
        >
          <div className={styles.wrap} data-active={active}>
            <div className={styles.commodityInfo}>
              <div className={styles.imgWrap}>
                <LazyLoadImg
                  src={ossImgCompress(commodity?.displays?.[0]?.url_s || defaultImg, {
                    width: 128,
                    height: 128,
                    format: webp ? 'webp' : 'jpg',
                  })}
                />
              </div>
              <div className={styles.itemWrap}>
                <div className={styles.nameWrap}>
                  <div
                    className={styles.name}
                    dangerouslySetInnerHTML={{
                      __html: getVersionName({
                        cids: commodity.id,
                        X_Language,
                        commodityName: commodity.commodityName,
                      }),
                    }}
                  />
                </div>
                <div className={styles.priceWrap}>
                  <div className={styles.price}>
                    {formatPriceThousands({
                      price: price.amount,
                      signal: price.signal,
                      currency: price.currency,
                      country,
                    })}
                  </div>
                  {!isEmpty(origin_price) && (
                    <div className={styles.origin}>
                      {formatPriceThousands({
                        price: origin_price.amount,
                        signal: origin_price.signal,
                        currency: origin_price.currency,
                        country,
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div
              ref={itemIntroRef}
              className={cls({
                [styles.itemIntro]: true,
                [styles.hide]: !active || !showItemIntro,
              })}
            >
              {showItemIntro && (
                <div className={styles.introList}>
                  {!isEmpty(campaignExtraInfo) && (
                    <ul className={styles.extraIntro}>
                      {campaignExtraInfo.map((text, k) => (
                        <li key={k}>
                          <span dangerouslySetInnerHTML={{ __html: text }} />
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className={styles.introWrap} dangerouslySetInnerHTML={{ __html: cinfos }} />
                </div>
              )}
            </div>
          </div>
        </LinearItemWrap>
      </ComponentsErrorBoundary>
      <div
        ref={giftsRef}
        className={cls({
          [styles.giftsWrap]: true,
          [styles.hide]: !active,
        })}
      >
        {active && <PrimeWrap cid={cid} />}
        <ItemGiftsWrap cid={cid} style={{ padding: '16px 0', margin: '0 24px', width: 'auto' }} />
        <CloudWrap commodity={commodity} />
      </div>
    </div>
  );
}

const VersionItemMob = inject('product', 'area', 'app', 'abtest', 'order')(observer(Index));

export default VersionItemMob;
